"use client";

import{ forwardRef } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
// Pixel-identical local copy — see assets/uiGlyphs for why.
import { ChevronDownIcon } from "@/assets/uiGlyphs";
import { cn } from "@/lib/utils";

export const classNameStyles = {
  triggerDefault: "",
  triggerAdmin:
    "w-full p-3 border border-gray-3 rounded-md bg-white text-black focus:border-primary-main focus:ring-2 focus:ring-primary-main/20 transition-colors flex items-center justify-between",
  contentDefault: "",
  /** z-[120] keeps the portalled dropdown above Dialog's z-[110] overlay/panel (admin forms inside modals) */
  contentAdmin:
    "bg-white border border-gray-3 rounded-md shadow-lg z-[120] w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)] overflow-hidden",
  viewportDefault: "",
  /** min-h-0 lets the flex viewport shrink so overflow-y scrolling works with long option lists */
  viewportAdmin: "p-1 min-h-0",
  itemDefault: "",
  itemAdmin:
    "px-3 py-2 text-sm text-black hover:bg-gray-1 focus:bg-gray-1 cursor-pointer rounded-sm",
  separatorDefault: "",
  separatorAdmin: "h-px bg-gray-3 my-1",
  labelDefault: "",
  labelAdmin: "px-3 py-2 text-sm font-semibold text-gray-5",
  /**
   * Lead-drop dropdown panel, matching the chandigarh portal's shadcn Select:
   * bordered white card, 384px max height, native viewport scrolling.
   * `bg-[#FFFFFF]` and not `bg-white` — the theme's `white` token is #f8f9fa,
   * which reads grey next to the page. z-[120] keeps the portalled panel above
   * the lead-drop Dialog overlay.
   */
  contentLead:
    "z-[120] max-h-96 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-[#E2E8F0] bg-[#FFFFFF] text-[#020617] shadow-md",
  viewportLead: "p-1",
  /** `relative` + `pr-8` reserve the row's own slot for the ✓ ItemIndicator. */
  itemLead:
    "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 font-poppins text-sm outline-none hover:bg-[#F1F5F9] focus:bg-[#F1F5F9] focus:text-[#0F172A] data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  labelLead: "px-2 py-1.5 font-poppins text-sm font-semibold text-[#171719]",
} as const;

type StyleKey = keyof typeof classNameStyles;

export const SelectRoot = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;
export const SelectPortal = SelectPrimitive.Portal;

export interface SelectProps extends SelectPrimitive.SelectProps {
  label?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  children,
  ...props
}) => {
  const selectContent = (
    <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>
  );

  if (label) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium pl-3 text-gray-700">
          {label}
          <span className="text-secondary-main">*</span>
        </label>
        {selectContent}
      </div>
    );
  }

  return selectContent;
};
Select.displayName = "Select";

export interface SelectTriggerProps extends SelectPrimitive.SelectTriggerProps {
  classType?: StyleKey;
  icon?: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
}
export const SelectTrigger = forwardRef<
  HTMLButtonElement,
  SelectTriggerProps
>(
  (
    {
      className,
      classType = "triggerDefault",
      icon: IconComponent,
      iconClassName,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(classNameStyles[classType], className)}
        {...props}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {IconComponent && <IconComponent className="h-4 w-4 text-gray-5 shrink-0" />}
          <div className="min-w-0 flex-1 overflow-hidden text-left [&>span]:block [&>span]:w-full [&>span]:overflow-hidden [&>span]:text-ellipsis [&>span]:whitespace-nowrap">
            {children}
          </div>
        </div>
        <SelectPrimitive.Icon asChild>
          <ChevronDownIcon
            className={cn("text-gray-5 shrink-0 size-4 lg:size-5", iconClassName)}
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    );
  }
);
SelectTrigger.displayName = "SelectTrigger";

export interface SelectContentProps extends SelectPrimitive.SelectContentProps {
  classType?: StyleKey;
  viewportClassType?: StyleKey;
  viewportClassName?: string;
}
export const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    classType?: keyof typeof classNameStyles;
    viewportClassType?: keyof typeof classNameStyles;
    viewportClassName?: string;
    position?: "popper" | "item-aligned";
    sideOffset?: number;
  }
>(
  (
    {
      className,
      classType = "contentDefault",
      viewportClassType = "viewportDefault",
      viewportClassName,
      position = "popper",
      sideOffset = 4,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          ref={ref}
          className={cn(
            // Content is portaled to <body>, so it competes on z-index with
            // other portaled overlays. Default above the Dialog layer
            // (z-[110]/z-[111]) so dropdowns are never hidden behind a modal
            // overlay. Overridable per-instance (e.g. admin uses z-50).
            "z-120",
            classNameStyles[classType],
            className,
          )}
          position={position}
          sideOffset={sideOffset}
          {...props}
        >
          <SelectPrimitive.Viewport
            className={cn(
              classNameStyles[viewportClassType],
              viewportClassName
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  }
);
SelectContent.displayName = "SelectContent";

export interface SelectItemProps extends SelectPrimitive.SelectItemProps {
  classType?: StyleKey;
}
export const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
  ({ className, classType = "itemDefault", children, ...props }, ref) => {
    return (
      <SelectPrimitive.Item
        ref={ref}
        className={cn(classNameStyles[classType], className)}
        {...props}
      >
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        <SelectPrimitive.ItemIndicator className="absolute right-2">
          ✓
        </SelectPrimitive.ItemIndicator>
      </SelectPrimitive.Item>
    );
  }
);
SelectItem.displayName = "SelectItem";

export const SelectSeparator = forwardRef<
  HTMLDivElement,
  SelectPrimitive.SelectSeparatorProps & { classType?: StyleKey }
>(({ className, classType = "separatorDefault", ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(classNameStyles[classType], className)}
    {...props}
  />
));
SelectSeparator.displayName = "SelectSeparator";

export const SelectLabel = forwardRef<
  HTMLDivElement,
  SelectPrimitive.SelectLabelProps & { classType?: StyleKey }
>(({ className, classType = "labelDefault", ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(classNameStyles[classType], className)}
    {...props}
  />
));
SelectLabel.displayName = "SelectLabel";
